import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Row, Button } from 'reactstrap';
import { api } from '../../utils/index';
import SetTitle from '../../utils/set_title';
import Loading from '../../tagging_tool/components/Loading';
import './MonitoringDashboard.scss';

// A redesigned card that can now display multiple sections
const DetailedStatCard = ({ sections }) => (
  <Col lg="6" xl="4" className="mb-4">
    <Card className="detailed-stat-card h-100 shadow">
      <CardBody>
        {sections.map((section, index) => (
          <div key={section.title}>
            {/* Section Header */}
            <Row className="align-items-center">
              <div className="col">
                <h3 className="mb-0">{section.title}</h3>
              </div>
              <div className="col-auto">
                <i className={`${section.iconClass} card-icon text-${section.color}`} />
              </div>
            </Row>
            {/* Section Body */}
            <div className="mt-3">
              {Object.entries(section.data).map(([key, value]) => (
                <div key={key} className="stat-item">
                  <span className="stat-key">{key.replace(/_/g, ' ')}</span>
                  <span className={`stat-value font-weight-bold text-${key === 'ERROR' && value > 0 ? 'danger' : 'default'}`}>{value}</span>
                </div>
              ))}
            </div>
            {/* Add a divider if it's not the last section */}
            {index < sections.length - 1 && <hr className="my-4" />}
          </div>
        ))}
      </CardBody>
    </Card>
  </Col>
);

const MonitoringDashboard = () => {
  SetTitle('Monitoring Dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const response = await api.get('/monitoring-dashboard/');
      setData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch monitoring data.');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const initialLoad = async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    };
    initialLoad();
  }, [fetchData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  if (loading) {
    return <Loading />;
  }

  if (error && !data) {
    return <div className="alert alert-danger">{error}</div>;
  }

  // Prepare data for the "General Tasks" card by removing the 'total' key
  const generalTasksData = data ? { ...data.q_tasks } : {};
  if (generalTasksData.total !== undefined) {
    delete generalTasksData.total;
  }

  // Prepare the sections for the merged cards
  const studioAndKeywordSections = data ? [
    { title: 'StudioKW Status', data: data.studio_kw, iconClass: 'fas fa-file-alt', color: 'warning' },
    { title: 'Keyword Status', data: { 'Waiting for Data': data.keyword_retrieving_data }, iconClass: 'fas fa-key', color: 'success' }
  ] : [];

  const volumeAndTaggingSections = data ? [
    { title: 'Volume Report Status', data: data.volume_report, iconClass: 'fas fa-chart-bar', color: 'info' },
    { title: 'Tagging Tool Status', data: data.tagging_tool, iconClass: 'fas fa-tags', color: 'danger' }
  ] : [];

  return (
    <>
      <div className="bg-gradient-info secondary-page-title">
        <div className="form-title d-flex justify-content-between align-items-center w-100">
          <div>
            <i className="ni ni-sound-wave text-white" style={{ fontSize: '35px' }}></i>
            <h1 className="page-title d-inline-block ml-3">Monitoring Dashboard</h1>
          </div>
          <div className="d-flex align-items-center">
            {lastUpdated && (
              <span className="text-white-50 mr-3 last-updated">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button
              color="light"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="mr-4"
            >
              {isRefreshing ? (
                <>
                  <i className="fas fa-sync-alt fa-spin mr-2" /> Refreshing...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt mr-2" /> Refresh
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      <div className="monitoring-dashboard-container mt-4">
        {data && (
          <Row>
            <DetailedStatCard sections={[{ title: "General Tasks Breakdown", data: generalTasksData, iconClass: "fas fa-tasks", color: "primary" }]} />
            <DetailedStatCard sections={studioAndKeywordSections} />
            <DetailedStatCard sections={volumeAndTaggingSections} />
          </Row>
        )}
      </div>
    </>
  );
};

export default MonitoringDashboard;